/**
 * AuditHistoryView — Admin-facing audit log viewer (BUG-265).
 *
 * Shows recent audit entries for the current tenant scoped to admin-level
 * mutations (company settings, user and role changes, org hierarchy).
 * Backed by GET /api/tenant-admin/audit-logs.
 *
 * SOC 2 CC7.2 — tenant-scoped audit trail exposure.
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatRoleName } from '../../utils/roleUtils';
import { capAuditText, labelForId } from '../../utils/auditDisplay';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 25;
const VALUE_TRUNCATE = 60;

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// BUG-265 (R2): readable event labels. Backend writes `<module>.{action}`;
// this map turns that into a sentence-case phrase a QA lead can scan fast.
const EVENT_LABELS = {
  'entity.create': 'Created',
  'entity.update': 'Updated',
  'entity.delete': 'Deleted',
  'entity.downloaded': 'Downloaded',
  'entity.role_assign': 'Role assigned',
  'entity.role_revoke': 'Role revoked',
  'entity.move': 'Moved',
  'user.create': 'User created',
  'user.update': 'User updated',
  'user.delete': 'User deleted',
  'user.role_assign': 'Role assigned',
  'user.role_revoke': 'Role revoked',
  'config.update': 'Settings updated',
  // Auth events
  'auth.login': 'Auth · Login',
  'auth.login_success': 'Auth · Login',
  'auth.login_failed': 'Auth · Login failed',
  'auth.logout': 'Auth · Logout',
  'auth.password_change': 'Auth · Password change',
  'auth.password_changed': 'Auth · Password change',
  'auth.switch_site': 'Auth · Switch site',
  'auth.agreement_accepted': 'Auth · Agreement accepted',
  // File events
  'files.upload': 'Files · Upload',
  'files.download': 'Files · Download',
  'files.delete': 'Files · Delete',
  'files.view': 'Files · View',
  'files.restore': 'Files · Restore',
};

const prettyEvent = (evt) => {
  if (!evt) return '—';
  const label = EVENT_LABELS[evt];
  if (label) return label;
  // Fallback: humanise the raw identifier
  return String(evt)
    .replace(/\./g, ' · ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Friendly labels for the entity-type filter and the Target column. Any
// `target_type` not listed here falls back to the raw slug, humanised.
const TARGET_LABELS = {
  tenant_settings: 'Company Settings',
  user: 'User',
  org_node: 'Org Hierarchy',
  role: 'Role',
  site_workforce: 'Workforce Data',
  workforce_request: 'Workforce Access Request',
  incident: 'Incident',
  hira: 'HIRA',
  capa: 'CAPA',
  ptw: 'Permit to Work',
  audit: 'Audit',
  document: 'Document',
  document_number_config: 'Document Numbering Config',
  document_workflow_template: 'Document Workflow Config',
  meeting: 'Meeting',
  training_course: 'Training Course',
  training_record: 'Training Record',
  certification: 'Certification',
  competency_matrix: 'Competency Matrix',
  hazard: 'Hazard',
  file: 'File',
  user_site_role: 'Site Role Assignment',
  site_module_permission: 'Site Module Permission',
  lca_project: 'LCA Project',
  mrv_asset: 'MRV Asset',
  mrv_activity_data: 'MRV Activity Data',
  mrv_emission_factor: 'MRV Emission Factor',
  mrv_emission_goal: 'MRV Goal',
  mrv_scenario: 'MRV Scenario',
  mrv_scenario_intervention: 'MRV Intervention',
  mrv_carbon_credit: 'MRV Carbon Credit',
  mrv_report: 'MRV Report',
  mrv_registry_submission: 'MRV Registry Submission',
  mrv_data_source: 'MRV Data Source',
  mrv_ingestion_run: 'MRV Ingestion Run',
  mrv_standardisation_rule: 'MRV Standardisation Rule',
  mrv_api_key: 'MRV API Key',
  mrv_webhook: 'MRV Webhook',
  mrv_connector: 'MRV Connector',
  ers_electricity: 'ERS · Electricity',
  ers_production: 'ERS · Production',
  ers_fuel: 'ERS · Fuel',
  ers_thermal: 'ERS · Thermal',
  ers_water: 'ERS · Water',
  ers_waste: 'ERS · Waste',
  ers_renewable: 'ERS · Renewable Asset',
  ers_seu_electrical: 'ERS · SEU Electrical',
  ers_seu_fuel: 'ERS · SEU Fuel',
  ers_seu_water: 'ERS · SEU Water',
  ers_seu_waste: 'ERS · SEU Waste',
  ers_equipment: 'ERS · Equipment',
  ers_baseline: 'ERS · Baseline',
};
const humanise = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const prettyTarget = (t) => TARGET_LABELS[t] || humanise(t) || '—';

const truncate = (val, max = VALUE_TRUNCATE) => {
  if (val === null || val === undefined) return '—';
  let s;
  if (typeof val === 'string') s = val;
  else if (typeof val === 'number' || typeof val === 'boolean') s = String(val);
  else s = JSON.stringify(val);
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
};

/**
 * The value for the expanded row, where there is room for the whole thing.
 *
 * Capped all the same: real snapshots in this system already carry values of
 * ~6,800 characters (a permit's safety checklist), and an unbounded dump would
 * push the rest of the row off the screen. The cap is generous enough that
 * ordinary values are never touched, and it says how much it withheld rather
 * than trailing off silently.
 */
const fullValue = (val, referenceNames) => {
  if (val === null || val === undefined || val === '') return '—';
  // A stored foreign key reads as a UUID. Show the record's name where we can
  // resolve it, keeping the id in the tooltip so the entry stays traceable.
  const label = labelForId(val, referenceNames);
  if (label) return <span title={val}>{label}</span>;
  let s;
  if (typeof val === 'string') s = val;
  else if (typeof val === 'number' || typeof val === 'boolean') s = String(val);
  else s = JSON.stringify(val, null, 2);
  return capAuditText(s, 'export the CSV for the full value');
};

/**
 * Which fields this entry changed.
 *
 * ONE definition, used by both the summary cell and the expanded detail, so
 * the two can never disagree about what changed — a summary saying "3 fields"
 * above a detail listing four is worse than either alone.
 *
 * Falls back through the columns in order of usefulness: the explicit
 * changed_fields list, then the keys present in the new or old value maps.
 */
const diffFields = (entry) => {
  const { old_values: oldV, new_values: newV, changed_fields: changed } = entry || {};
  if (Array.isArray(changed) && changed.length) return changed;
  if (newV && typeof newV === 'object') return Object.keys(newV);
  if (oldV && typeof oldV === 'object') return Object.keys(oldV);
  return [];
};

/** Is there more to show than the summary cell already shows? */
const hasExpandableDetail = (entry) => {
  if (diffFields(entry).length) return true;
  const snap = entry?.context && typeof entry.context === 'object'
    ? entry.context.snapshot : null;
  return Boolean(snap && typeof snap === 'object' && Object.keys(snap).length);
};

// Auth/file/RBAC events don't carry field diffs, but their context JSON
// holds the bits that actually matter (reason, scope, IP, sites swapped).
// Surface those first so the Summary column isn't a row of em-dashes.
const renderAuthContext = (entry) => {
  const ctx = entry.context;
  if (!ctx || typeof ctx !== 'object') return null;
  if (ctx.module !== 'auth') return null;
  const items = [];
  if (ctx.reason) items.push({ k: 'Reason', v: String(ctx.reason).replace(/_/g, ' ') });
  if (ctx.scope_type) items.push({ k: 'Scope', v: ctx.scope_type });
  if (ctx.new_site_name) items.push({ k: 'Site', v: `${ctx.old_site_name || '—'} → ${ctx.new_site_name}` });
  if (ctx.tenant_status) items.push({ k: 'Tenant', v: ctx.tenant_status });
  if (entry.ip_address) items.push({ k: 'IP', v: entry.ip_address });
  if (!items.length) return null;
  return (
    <ul className="space-y-0.5 text-xs text-gray-700">
      {items.map(({ k, v }) => (
        <li key={k}>
          <span className="font-semibold text-gray-800">{k}:</span>{' '}
          <span className="text-gray-700">{v}</span>
        </li>
      ))}
    </ul>
  );
};

// BUG-265 (R2): render the "what changed" detail. Three priorities:
//  1. Field-level diff from old_values/new_values/changed_fields.
//  2. Narrative from context.description (already human-readable server-side).
//  3. Fallback to raw field list.
const renderChanges = (entry) => {
  const { old_values, new_values, context } = entry;

  // 0. Auth context (no field diff exists for login/logout/etc.)
  const authCtx = renderAuthContext(entry);
  if (authCtx) return authCtx;

  // 1. Field-level diff — the same list the expanded row renders in full.
  const fields = diffFields(entry);

  if (fields && fields.length) {
    return (
      <ul className="space-y-0.5 text-xs text-gray-700">
        {fields.slice(0, 6).map((f) => {
          const oldV = old_values && f in old_values ? old_values[f] : undefined;
          const newV = new_values && f in new_values ? new_values[f] : undefined;
          const oldS = oldV === undefined ? '—' : truncate(oldV);
          const newS = newV === undefined ? '—' : truncate(newV);
          return (
            <li key={f}>
              <span className="font-semibold text-gray-800">{f}:</span>{' '}
              <span className="text-gray-500 line-through">{oldS}</span>{' '}
              <span className="text-gray-400">→</span>{' '}
              <span className="text-gray-800">{newS}</span>
            </li>
          );
        })}
        {fields.length > 6 && (
          <li className="text-gray-400 italic">+{fields.length - 6} more field(s)</li>
        )}
      </ul>
    );
  }

  // 2. Description narrative (humanised server-side)
  const desc = context && typeof context === 'object' && context.description;
  if (desc) {
    // Humanise any slug still present in the description (best-effort).
    const humanised = String(desc).replace(
      /\b([a-z][a-z0-9]*(?:_[a-z0-9]+)+)\b/g,
      (match) => formatRoleName(match)
    );
    return <span className="text-xs text-gray-700">{humanised}</span>;
  }

  // 3. Snapshot summary (role assign/revoke path)
  const snap = context && typeof context === 'object' && context.snapshot;
  if (snap && typeof snap === 'object') {
    const roleLabel = snap.role_display_name || formatRoleName(snap.role_name) || snap.role_id;
    const targetLabel = snap.target_user_email || snap.target_user_id || '';
    if (roleLabel || targetLabel) {
      return (
        <span className="text-xs text-gray-700">
          {roleLabel && <><span className="font-semibold">{roleLabel}</span></>}
          {targetLabel && <> → <span className="text-gray-600">{targetLabel}</span></>}
        </span>
      );
    }
  }

  return <span className="text-xs text-gray-400">—</span>;
};

/**
 * The expanded detail for one entry: every changed field, previous and new
 * value side by side, untruncated.
 *
 * The summary cell has to fit a table row, so it caps at six fields and clips
 * each value to 60 characters. That is fine for scanning and useless for
 * answering "what exactly did this become" — which is the question an audit
 * trail exists to answer. This is where the full values live.
 */
const renderDetail = (entry, referenceNames) => {
  const fields = diffFields(entry);
  const oldV = entry.old_values && typeof entry.old_values === 'object' ? entry.old_values : {};
  const newV = entry.new_values && typeof entry.new_values === 'object' ? entry.new_values : {};
  const snap = entry.context && typeof entry.context === 'object' ? entry.context.snapshot : null;

  return (
    <div className="space-y-4">
      {fields.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {fields.length} field{fields.length === 1 ? '' : 's'} changed
          </div>
          <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold w-1/4">Field</th>
                  <th className="px-3 py-2 text-left font-semibold">Previous value</th>
                  <th className="px-3 py-2 text-left font-semibold">New value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {fields.map((f) => {
                  const before = f in oldV ? oldV[f] : undefined;
                  const after = f in newV ? newV[f] : undefined;
                  return (
                    <tr key={f} className="align-top">
                      <td className="px-3 py-2 font-semibold text-gray-800 whitespace-nowrap">
                        {humanise(f)}
                      </td>
                      {/* break-words, not truncate: the whole point of the
                          expanded view is that nothing is hidden. */}
                      <td className="px-3 py-2 text-gray-500 line-through whitespace-pre-wrap break-words">
                        {fullValue(before, referenceNames)}
                      </td>
                      <td className="px-3 py-2 text-gray-900 whitespace-pre-wrap break-words">
                        {fullValue(after, referenceNames)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* The snapshot is the record's state as recorded with the event. Shown
          below the diff for context, and it is the ONLY thing available for
          events that carry no diff — a delete, or an older entry written
          before the diff was captured. */}
      {snap && typeof snap === 'object' && Object.keys(snap).length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
            {fields.length ? 'Record at the time of the change' : 'Recorded details'}
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 rounded-md
                         border border-gray-200 bg-white px-3 py-2 text-xs">
            {Object.entries(snap).map(([k, v]) => (
              <div key={k} className="flex gap-2 min-w-0">
                <dt className="text-gray-500 shrink-0">{humanise(k)}:</dt>
                <dd className="text-gray-800 min-w-0 break-words">{fullValue(v, referenceNames)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500">
        {entry.ip_address && <span>From <span className="font-mono">{entry.ip_address}</span></span>}
        {entry.target_id && (
          <span>Record id <span className="font-mono">{entry.target_id}</span></span>
        )}
        {entry.user_agent && (
          <span className="truncate max-w-[26rem]" title={entry.user_agent}>
            {entry.user_agent}
          </span>
        )}
      </div>
    </div>
  );
};

// Pull the structured details out of the audit row so the UI can render
// period/site context above the field-level diff. Backend writes this
// payload either directly under `context` (legacy entries) or nested under
// `context.details` (newer workforce emits) — accept both shapes.
const extractDetails = (entry) => {
  const ctx = entry?.context;
  if (!ctx || typeof ctx !== 'object') return null;
  if (ctx.details && typeof ctx.details === 'object') return ctx.details;
  return ctx;
};

const renderTargetMeta = (entry) => {
  const d = extractDetails(entry);
  if (!d) return null;
  const period = d.period || null;
  const site = d.site_name || null;
  if (!period && !site) return null;
  return (
    <div className="text-xs text-gray-600 mt-1">
      {site && <span><span className="font-semibold">Site:</span> {site}</span>}
      {site && period && <span className="mx-1 text-gray-300">·</span>}
      {period && <span><span className="font-semibold">Period:</span> {period}</span>}
    </div>
  );
};

export default function AuditHistoryView() {
  const [entries, setEntries] = useState([]);
  // Referenced record id -> display name, from the API. Empty is fine: the
  // detail rows then show the raw id, which is what they did before.
  const [referenceNames, setReferenceNames] = useState({});
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageInput, setPageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [entityOptions, setEntityOptions] = useState([]);
  const [exporting, setExporting] = useState(false);
  // Rows the user has opened. A Set rather than a single id so several can be
  // compared at once — the common task is "what changed here versus there".
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const fetchEntries = useCallback(async (nextOffset = 0, sizeOverride) => {
    setLoading(true);
    setError(null);
    const effectiveSize = sizeOverride ?? pageSize;
    try {
      const params = { limit: effectiveSize, offset: nextOffset };
      if (entityFilter) params.entity_type = entityFilter;
      const res = await axios.get(`${API}/tenant-admin/audit-logs`, { params });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      // Referenced ids -> names, resolved by the API inside this tenant so the
      // detail rows can show "Engineering" instead of a department UUID.
      setReferenceNames(res.data.reference_names || {});
      setOffset(nextOffset);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load audit history');
    } finally {
      setLoading(false);
    }
  }, [entityFilter, pageSize]);

  // Populate the entity-type dropdown from the values that actually exist
  // in this tenant's audit_logs table — avoids the previous hardcoded
  // 3-option list that hid most modules from the filter.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/tenant-admin/audit-logs/entity-types`);
        if (!cancelled) setEntityOptions(res.data?.entity_types || []);
      } catch {
        if (!cancelled) setEntityOptions([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { fetchEntries(0); }, [fetchEntries]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = {};
      if (entityFilter) params.entity_type = entityFilter;
      const res = await axios.get(`${API}/tenant-admin/audit-logs/export.csv`, {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `audit-logs-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  }, [entityFilter]);

  const canPrev = offset > 0 && !loading;
  const canNext = offset + pageSize < total && !loading;
  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + entries.length, total);

  const goToPage = (page1Based) => {
    if (loading) return;
    const clamped = Math.max(1, Math.min(totalPages, page1Based));
    fetchEntries((clamped - 1) * pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    // Re-anchor on the first row of the current page under the new size.
    const newOffset = Math.floor(offset / newSize) * newSize;
    fetchEntries(newOffset, newSize);
    setPageInput('');
  };

  const handlePageJump = (e) => {
    e.preventDefault();
    const n = parseInt(pageInput, 10);
    if (Number.isFinite(n)) {
      goToPage(n);
      setPageInput('');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Audit History</h2>
          <p className="text-sm text-gray-500 mt-1">
            Recent admin actions: company settings, user changes, role assignments, org hierarchy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All entities</option>
            {entityOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {prettyTarget(opt.value)}
                {typeof opt.count === 'number' ? ` (${opt.count})` : ''}
              </option>
            ))}
          </select>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            title="Export current filter to CSV"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button
            onClick={() => fetchEntries(0)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-2 py-2 w-8"><span className="sr-only">Expand</span></th>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">Actor</th>
              <th className="px-3 py-2 text-left">Event</th>
              <th className="px-3 py-2 text-left">Target</th>
              <th className="px-3 py-2 text-left w-1/3">Summary</th>
              <th className="px-3 py-2 text-left">Result</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td className="px-3 py-4 text-gray-400" colSpan={7}>Loading…</td></tr>
            ) : entries.length === 0 ? (
              <tr><td className="px-3 py-4 text-gray-400" colSpan={7}>No entries.</td></tr>
            ) : entries.map((e) => {
              const expandable = hasExpandableDetail(e);
              const open = expandedIds.has(e.id);
              return (
              <React.Fragment key={e.id}>
              <tr
                className={`align-top ${open ? 'bg-blue-50/40' : 'hover:bg-gray-50'} ${
                  expandable ? 'cursor-pointer' : ''}`}
                onClick={expandable ? () => toggleExpanded(e.id) : undefined}
              >
                <td className="px-2 py-2">
                  {expandable ? (
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-label={open ? 'Hide changed values' : 'Show changed values'}
                      onClick={(ev) => { ev.stopPropagation(); toggleExpanded(e.id); }}
                      className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700
                                 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  ) : (
                    /* No expander when there is nothing behind it — an arrow
                       that opens an empty panel is worse than no arrow. */
                    <span className="sr-only">No further detail</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{formatWhen(e.event_time)}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{e.actor_name || e.actor_email || e.actor_id}</div>
                  <div className="text-xs text-gray-500">{e.actor_role}</div>
                </td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{prettyEvent(e.event_type)}</td>
                <td className="px-3 py-2">
                  <div className="text-gray-700">{prettyTarget(e.target_type)}</div>
                  <div className="text-xs text-gray-500 truncate max-w-[180px]" title={e.target_id || ''}>
                    {e.target_name || e.target_id || ''}
                  </div>
                  {renderTargetMeta(e)}
                </td>
                <td className="px-3 py-2 text-gray-700 align-top">{renderChanges(e)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                    e.result === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {e.result || 'success'}
                  </span>
                </td>
              </tr>
              {open && (
                <tr className="bg-blue-50/40">
                  <td />
                  <td colSpan={6} className="px-3 pb-4 pt-1">
                    {renderDetail(e, referenceNames)}
                  </td>
                </tr>
              )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-gray-600">
        <div className="flex items-center gap-3">
          <span>
            {total > 0
              ? <>Showing <span className="font-semibold">{showingFrom}</span>&ndash;<span className="font-semibold">{showingTo}</span> of <span className="font-semibold">{total.toLocaleString()}</span></>
              : 'No entries'}
          </span>
          <span className="flex items-center gap-1.5 text-gray-500">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              disabled={loading}
              className="px-2 py-1 border border-gray-300 rounded text-xs"
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => goToPage(1)}
            disabled={!canPrev}
            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
            title="First page"
          >
            «
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={!canPrev}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-gray-700">
            Page <span className="font-semibold">{currentPage}</span> of <span className="font-semibold">{totalPages.toLocaleString()}</span>
          </span>
          <form onSubmit={handlePageJump} className="flex items-center gap-1">
            <input
              type="number"
              min="1"
              max={totalPages}
              value={pageInput}
              onChange={(e) => setPageInput(e.target.value)}
              placeholder="Go to"
              className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-center"
              disabled={loading || totalPages <= 1}
            />
          </form>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={!canNext}
            className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
          >
            Next
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={!canNext}
            className="px-2 py-1 border border-gray-300 rounded disabled:opacity-40"
            title="Last page"
          >
            »
          </button>
        </div>
      </div>
    </div>
  );
}
